import { StreamMessage, useChannelStateContext, useTranslationContext } from '../../../context';
import { DefaultBeloChatGenerics } from '../../../types/types';
import { Channel, ReactionResponse } from 'belo-js-sdk';

export const MAX_MESSAGE_REACTIONS_TO_FETCH = 1200;

type FetchMessageReactionsNotifications<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = {
  getErrorNotification?: (message: StreamMessage<BeloChatGenerics>) => string;
  notify?: (notificationText: string, type: 'success' | 'error') => void;
};

export function useReactionsFetcher<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  message: StreamMessage<BeloChatGenerics>,
  notifications: FetchMessageReactionsNotifications<BeloChatGenerics> = {},
) {
  const { channel } = useChannelStateContext<BeloChatGenerics>('useReactionFetcher');
  const { t } = useTranslationContext('useReactionFetcher');
  const { getErrorNotification, notify } = notifications;

  return async () => {
    try {
      return await fetchMessageReactions(channel, message.id);
    } catch (e) {
      const errorMessage = getErrorNotification?.(message);
      notify?.(errorMessage || t('Error fetching reactions'), 'error');
      throw e;
    }
  };
}

async function fetchMessageReactions<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(channel: Channel<BeloChatGenerics>, messageId: string) {
  const reactions: ReactionResponse<BeloChatGenerics>[] = [];
  const limit = 300;
  let offset = 0;
  const reactionsLimit = MAX_MESSAGE_REACTIONS_TO_FETCH;
  let lastPageSize = limit;

  while (lastPageSize === limit && reactions.length < reactionsLimit) {
    const response = await channel.getReactions(messageId, {
      limit,
      offset,
    });
    lastPageSize = response.reactions.length;
    if (lastPageSize > 0) {
      reactions.push(...response.reactions);
    }
    offset += lastPageSize;
  }

  return reactions;
}
